import { useCallback, useEffect, useRef, useState } from "react";
import api from "./api";

// Per-user UI preferences, stored server-side on the user record so an
// arrangement survives reload and follows the user across devices.
export function usePrefs() {
  const [prefs, setPrefs] = useState(null);
  const ref = useRef({});

  useEffect(() => {
    let live = true;
    api.get("/prefs")
      .then((r) => { if (live) { ref.current = r.data?.prefs || {}; setPrefs(ref.current); } })
      .catch(() => { if (live) setPrefs({}); });
    return () => { live = false; };
  }, []);

  const savePref = useCallback(async (key, value) => {
    const next = { ...ref.current, [key]: value };
    ref.current = next;
    setPrefs(next);
    try { await api.put("/prefs", { prefs: next }); } catch { /* order stays local this session */ }
  }, []);

  return { prefs, savePref };
}

// Order `items` by a saved list of ids; anything unknown keeps its natural
// position at the end, so new projects always show up.
export function applyOrder(items, order) {
  if (!order?.length) return items;
  const rank = new Map(order.map((id, i) => [id, i]));
  return [...items].sort((a, b) => {
    const ra = rank.has(a.id) ? rank.get(a.id) : Infinity;
    const rb = rank.has(b.id) ? rank.get(b.id) : Infinity;
    if (ra !== rb) return ra - rb;
    return 0;
  });
}

// Swap an item with its neighbour, returning the new id order.
export function moveInList(ids, index, delta) {
  const target = index + delta;
  if (target < 0 || target >= ids.length) return null;
  const next = [...ids];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}
